import { type RefObject, useCallback, useEffect, useRef, useState } from 'react';

const HAS_EVER_SAVED_KEY = 'hasEverSavedItem';
const WIGGLE_DELAY_MS = 800;
const WIGGLE_DURATION_MS = 1300;

type Params = {
    buttonRef: RefObject<HTMLButtonElement | null>;
    /** Must be true for animations to run. When false all effects are skipped. */
    enabled: boolean;
    isFavorite: boolean;
    /**
     * The current lot's item ID. When this changes the per-lot wiggle state is
     * reset so the nudge re-arms on client-side lot-to-lot navigation.
     */
    itemId: number;
};

type Return = {
    /** Apply .save-button-activating class — true for 500ms after a save */
    isActivating: boolean;
    /** Apply .save-button-wiggle class — true for wiggle duration */
    isWiggling: boolean;
    onButtonFocus: () => void;
    onButtonMouseEnter: () => void;
};

/**
 * Orchestrates the primary LDP Save button animations:
 *  - Idle wiggle nudge (first-time savers only, gated by IntersectionObserver + visibilitychange)
 *  - Post-save button pulse + heart pop (CSS via isActivating class)
 *  - Post-save confetti burst (canvas, imperative ref)
 *  - localStorage persistence of hasEverSavedItem flag
 *
 * All animations are suppressed when prefers-reduced-motion is active.
 * This hook is a no-op when not mounted in the primary LDP context
 * (enableSaveAnimations guards the call site in ItemCardSaveIcon).
 */
export const useSaveAnimations = ({ buttonRef, enabled, isFavorite, itemId }: Params): Return => {
    const [isWiggling, setIsWiggling] = useState(false);
    const [isActivating, setIsActivating] = useState(false);

    const hasWiggledRef = useRef(false);
    const wiggleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const remainingMsRef = useRef(WIGGLE_DELAY_MS);
    const timerStartRef = useRef<number | null>(null);
    const observerRef = useRef<IntersectionObserver | null>(null);
    // Capture isFavorite at mount to block wiggle when item is already saved
    const initialFavoriteRef = useRef(isFavorite);
    // Baseline for save-transition detection, rebased per-lot below so switching lots
    // (e.g. next lot and back) never looks like a fresh false -> true "save".
    const prevIsFavoriteRef = useRef(isFavorite);

    const cancelWiggle = useCallback(() => {
        if (wiggleTimerRef.current !== null) {
            clearTimeout(wiggleTimerRef.current);
            wiggleTimerRef.current = null;
        }
    }, []);

    const startWiggleTimer = useCallback(() => {
        cancelWiggle();
        timerStartRef.current = Date.now();
        wiggleTimerRef.current = setTimeout(() => {
            if (!hasWiggledRef.current) {
                hasWiggledRef.current = true;
                setIsWiggling(true);
                setTimeout(() => {
                    setIsWiggling(false);
                }, WIGGLE_DURATION_MS);
            }
        }, remainingMsRef.current);
    }, [cancelWiggle]);

    const onButtonMouseEnter = useCallback(() => {
        cancelWiggle();
    }, [cancelWiggle]);

    const onButtonFocus = useCallback(() => {
        cancelWiggle();
    }, [cancelWiggle]);

    // Tracks the previous itemId so we can detect lot-to-lot navigation.
    // Using a ref avoids adding itemId to the wiggle setup effect deps in a way
    // that would cause it to re-run on every render.
    const prevItemIdRef = useRef(itemId);

    // Reset per-lot wiggle state when the displayed lot changes.
    // Must be declared BEFORE the wiggle setup effect so React runs this setup
    // first, ensuring hasWiggledRef and initialFavoriteRef are already reset
    // by the time the observer is re-attached.
    useEffect(() => {
        if (!enabled) {
            return;
        }
        if (prevItemIdRef.current === itemId) {
            return;
        }
        prevItemIdRef.current = itemId;
        hasWiggledRef.current = false;
        remainingMsRef.current = WIGGLE_DELAY_MS;
        initialFavoriteRef.current = isFavorite;
        // Rebase the save-transition baseline to the new lot's current state so the
        // detection effect below can't mistake "switched to an already-saved lot" for a save.
        prevIsFavoriteRef.current = isFavorite;
        cancelWiggle();
    }, [cancelWiggle, enabled, isFavorite, itemId]);

    // Wiggle setup: IntersectionObserver + visibilitychange pause/resume
    useEffect(() => {
        if (!enabled) {
            return;
        }
        // Don't wiggle if the user has ever saved before (localStorage flag)
        if (localStorage.getItem(HAS_EVER_SAVED_KEY)) {
            return;
        }
        // Don't wiggle if the item was already saved when the page loaded
        if (initialFavoriteRef.current) {
            return;
        }

        const button = buttonRef.current;
        if (!button) {
            return;
        }

        const handleVisibilityChange = () => {
            if (document.hidden) {
                // Pause: store remaining time
                if (timerStartRef.current !== null) {
                    remainingMsRef.current = Math.max(0, remainingMsRef.current - (Date.now() - timerStartRef.current));
                    cancelWiggle();
                }
            } else {
                // Resume: restart timer with remaining time
                startWiggleTimer();
            }
        };

        const observer = new IntersectionObserver(
            (entries) => {
                const entry = entries[0];
                if (entry.isIntersecting && !hasWiggledRef.current) {
                    document.addEventListener('visibilitychange', handleVisibilityChange);
                    startWiggleTimer();
                }
            },
            { threshold: 0.5 }
        );

        observer.observe(button);
        observerRef.current = observer;

        return () => {
            observer.disconnect();
            cancelWiggle();
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [buttonRef, cancelWiggle, enabled, itemId, startWiggleTimer]);

    // Save success detection: unsaved → saved transition.
    // Reads/rebases prevIsFavoriteRef itself (rather than via usePrevious) so the
    // comparison is always against this same lot's last known state - the itemId-change
    // effect above already rebased it to the new lot before this effect runs in the same commit.
    useEffect(() => {
        if (!enabled) {
            return;
        }

        const wasFavorite = prevIsFavoriteRef.current;
        prevIsFavoriteRef.current = isFavorite;

        if (wasFavorite !== false || isFavorite !== true) {
            return;
        }

        // Persist first-save flag
        if (!localStorage.getItem(HAS_EVER_SAVED_KEY)) {
            localStorage.setItem(HAS_EVER_SAVED_KEY, 'true');
        }

        // Cancel any pending wiggle
        cancelWiggle();

        const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (prefersReduced) {
            return;
        }

        // Trigger button pulse + heart pop via isActivating prop on LabeledAnimatedFavoriteButton
        setIsActivating(true);
        setTimeout(() => {
            setIsActivating(false);
        }, 500);
    }, [isFavorite, cancelWiggle, enabled]);

    return { isActivating, isWiggling, onButtonFocus, onButtonMouseEnter };
};
